import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, GraduationCap, Sun, Briefcase, CalendarOff, Plus, X, MapPin, Package, BookUser, FileDown, Timer } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { getAvailability, createPlanningEvent, updatePlanningEvent, deletePlanningEvent, getEventTypes, createEventType, deleteEventType, getEquipment, getDirectoryContacts, downloadEventCallsheet, uploadEventAttachment } from "../../services/api";
import { STATUS_META, PRIORITY_META } from "../../utils/eventVisuals";
import { useAuth } from "../../context/AuthContext";
import { EventAttachments } from "./EventAttachments";
import { EventDetailsDialog } from "./EventDetailsDialog";

const EVENT_COLORS = ["#7C3AED", "#e91e63", "#9c27b0", "#3f51b5", "#2196f3", "#00bcd4", "#009688",
  "#4caf50", "#8bc34a", "#ff9800", "#ff5722", "#f44336", "#607d8b"];

const STATUS_BADGE = {
  work: { className: "bg-green-100 text-green-700", icon: Briefcase },
  off: { className: "bg-slate-100 text-slate-500", icon: Sun },
  school: { className: "bg-amber-100 text-amber-700", icon: GraduationCap },
  absence: { className: "bg-red-100 text-red-600", icon: CalendarOff },
};

const LiveChrono = ({ since, base }) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const secs = (base || 0) + Math.max(0, Math.floor((Date.now() - new Date(since).getTime()) / 1000));
  const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60), s = secs % 60;
  return (
    <span className="flex items-center gap-1 font-mono font-bold text-amber-600 tabular-nums" data-testid="assignment-chrono">
      <Timer size={11} /> {String(h).padStart(2, "0")}:{String(m).padStart(2, "0")}:{String(s).padStart(2, "0")}
    </span>
  );
};

const EventEditor = ({ open, onClose, event, defaultDate, onSaved }) => {
  const [pendingFiles, setPendingFiles] = useState([]);
  const [createdEvent, setCreatedEvent] = useState(null);
  const [fileBusy, setFileBusy] = useState(false);
  const [attachmentRefresh, setAttachmentRefresh] = useState(0);
  const savingGuard = useRef(false);
  const [title, setTitle] = useState("");
  const [eventType, setEventType] = useState("tournage");
  const [color, setColor] = useState("#e91e63");
  const [date, setDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [status, setStatus] = useState("todo");
  const [priority, setPriority] = useState("normal");
  const [visibility, setVisibility] = useState("all");
  const [forceAssign, setForceAssign] = useState(false);
  const [employeeIds, setEmployeeIds] = useState([]);
  const [equipmentItems, setEquipmentItems] = useState([]);
  const [allEquipment, setAllEquipment] = useState([]);
  const [contactIds, setContactIds] = useState([]);
  const [allContacts, setAllContacts] = useState([]);
  const [contactToAdd, setContactToAdd] = useState("");
  const [equipToAdd, setEquipToAdd] = useState("");
  const [availability, setAvailability] = useState([]);
  const [saving, setSaving] = useState(false);
  const [types, setTypes] = useState([]);
  const [showTypeManager, setShowTypeManager] = useState(false);
  const [newTypeName, setNewTypeName] = useState("");
  const [newTypeColor, setNewTypeColor] = useState("#e91e63");

  const loadTypes = () => getEventTypes().then((res) => setTypes(res.data)).catch(() => {});

  useEffect(() => {
    if (open) {
      setPendingFiles([]); setCreatedEvent(null); setAttachmentRefresh(0);
      setTitle(event?.title || "");
      setEventType(event?.event_type || "");
      setColor(event?.color || "#e91e63");
      setDate(event?.date || defaultDate || new Date().toISOString().slice(0, 10));
      setEndDate(event?.end_date || "");
      setStartTime(event?.start_time || "");
      setEndTime(event?.end_time || "");
      setDescription(event?.description || "");
      setLocation(event?.location || "");
      setStatus(event?.status || "todo");
      setPriority(event?.priority || "normal");
      setVisibility(event?.visibility || "all");
      setForceAssign(false);
      setEmployeeIds(event?.employee_ids || []);
      setEquipmentItems(event?.equipment_items || []);
      setEquipToAdd("");
      setContactIds(event?.contact_ids || []);
      setContactToAdd("");
      setShowTypeManager(false);
      loadTypes();
      getEquipment().then((res) => setAllEquipment(res.data)).catch(() => {});
      getDirectoryContacts().then((res) => setAllContacts(res.data)).catch(() => {});
    }
  }, [open, event, defaultDate]);

  const selectType = (name) => {
    setEventType(name);
    const t = types.find((x) => x.name === name);
    if (t) setColor(t.color);
  };

  const addType = async () => {
    if (!newTypeName.trim()) { toast.error("Nom du type requis"); return; }
    try {
      const res = await createEventType({ name: newTypeName.trim(), color: newTypeColor });
      setNewTypeName("");
      await loadTypes();
      selectTypeFromCreated(res.data);
      toast.success("Type créé");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const selectTypeFromCreated = (t) => {
    setEventType(t.name);
    setColor(t.color);
  };

  const removeType = async (t) => {
    if (!window.confirm(`Supprimer le type "${t.name}" ? (les événements existants ne sont pas modifiés)`)) return;
    try {
      await deleteEventType(t.id);
      loadTypes();
    } catch {
      toast.error("Erreur");
    }
  };

  useEffect(() => {
    if (open && date) {
      getAvailability(date).then((res) => setAvailability(res.data.employees)).catch(() => {});
    }
  }, [open, date]);

  const toggleEmployee = (id) => {
    setEmployeeIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const addEquipment = () => {
    const eq = allEquipment.find((e) => e.id === equipToAdd);
    if (!eq) return;
    setEquipmentItems((prev) => [...prev, { equipment_id: eq.id, name: eq.name, quantity: 1 }]);
    setEquipToAdd("");
  };

  const setEquipQty = (id, qty) => {
    setEquipmentItems((prev) => prev.map((x) => x.equipment_id === id ? { ...x, quantity: Math.max(1, Number(qty) || 1) } : x));
  };

  const removeEquipment = (id) => {
    setEquipmentItems((prev) => prev.filter((x) => x.equipment_id !== id));
  };

  const handleSave = async () => {
    if (savingGuard.current || fileBusy) return;
    if (!title.trim()) { toast.error("Titre requis"); return; }
    if (!eventType) { toast.error("Choisissez un type"); return; }
    if (!date) { toast.error("Date requise"); return; }
    if (endDate && endDate < date) { toast.error("La date de fin doit être après le début"); return; }
    savingGuard.current = true;
    let saved = event || createdEvent;
    try {
      setSaving(true);
      const payload = {
        title, event_type: eventType, color, date,
        end_date: endDate || null, start_time: startTime || null, end_time: endTime || null,
        description: description || null, employee_ids: employeeIds,
        location: location || null, status, priority,
        equipment_items: equipmentItems,
        contact_ids: contactIds,
        visibility,
      };
      if (saved) {
        await updatePlanningEvent(saved.id, payload);
      } else {
        saved = (await createPlanningEvent(payload)).data;
        setCreatedEvent(saved);
      }
      for (const file of pendingFiles) {
        await uploadEventAttachment(saved.id, file.file);
        setPendingFiles(previous => previous.filter(item => item.id !== file.id));
        setAttachmentRefresh(value => value + 1);
      }
      toast.success(event || createdEvent ? "Événement modifié" : "Événement créé");
      onSaved();
      onClose();
    } catch (err) {
      if (saved && pendingFiles.length) { onSaved(); toast.error("Événement enregistré, mais un fichier n’a pas pu être envoyé. Réessayez l’enregistrement pour les fichiers restants."); }
      else toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
      savingGuard.current = false;
    }
  };

  const downloadCallsheet = async () => {
    try {
      const res = await downloadEventCallsheet(event.id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fiche-${(event.title || "evenement").replace(/[^a-zA-Z0-9_-]+/g, "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Erreur lors de la génération de la fiche PDF");
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Supprimer "${event.title}" ?`)) return;    try {
      await deletePlanningEvent(event.id);
      toast.success("Événement supprimé");
      onSaved();
      onClose();
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !saving && onClose()}>
      <DialogContent className="responsive-dialog max-w-lg max-h-[90dvh] overflow-y-auto" data-testid="event-dialog" onOpenAutoFocus={e => { if (window.innerWidth < 1024) e.preventDefault(); }}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-6 text-left" data-testid="event-dialog-title">
            <span className="w-3.5 h-3.5 rounded-full" style={{ backgroundColor: color }}></span>
            {event || createdEvent ? "Modifier l'événement" : "Nouvel événement"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="min-w-0">
              <Label className="mb-1 block text-xs">Titre</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex : Tournage com" data-testid="event-title-input" />
            </div>
            <div className="min-w-0">
              <Label className="mb-1 block text-xs">Type</Label>
              <Select value={eventType} onValueChange={selectType}>
                <SelectTrigger data-testid="event-type-select"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                <SelectContent>
                  {types.map((t) => (
                    <SelectItem key={t.id} value={t.name} data-testid={`event-type-option-${t.id}`}>
                      <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: t.color }}></span>{t.name}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <button className="text-[10px] text-teal-600 hover:underline mt-0.5" onClick={() => setShowTypeManager(!showTypeManager)} data-testid="manage-types-btn">
                {showTypeManager ? "Fermer" : "Gérer les types"}
              </button>
            </div>
          </div>

          {showTypeManager && (
            <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/60 space-y-2" data-testid="type-manager">
              <p className="text-xs font-semibold text-slate-600">Types d'événements</p>
              <div className="flex flex-wrap gap-1.5">
                {types.map((t) => (
                  <span key={t.id} className="flex items-center gap-1.5 text-xs text-white px-2.5 py-1 rounded-full" style={{ backgroundColor: t.color }}>
                    {t.name}
                    <button className="hover:opacity-70" onClick={() => removeType(t)} data-testid={`delete-type-${t.id}`}><X size={11} /></button>
                  </span>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Input className="h-8 text-xs flex-1" placeholder="Nom du nouveau type (ex : Formation)" value={newTypeName} onChange={(e) => setNewTypeName(e.target.value)} data-testid="new-type-name-input" />
                <div className="flex gap-1">
                  {EVENT_COLORS.slice(0, 6).map((c) => (
                    <button key={c} className={`w-7 h-7 rounded-full ${newTypeColor === c ? "ring-2 ring-offset-1 ring-slate-700" : ""}`} style={{ backgroundColor: c }} onClick={() => setNewTypeColor(c)} data-testid={`event-new-type-color-${c.slice(1)}`} aria-label={`Couleur ${c}`} />
                  ))}
                </div>
                <Button size="sm" className="h-8 bg-[#26788A] hover:bg-[#1D5E6C] text-white" onClick={addType} data-testid="add-type-btn"><Plus size={13} /></Button>
              </div>
            </div>
          )}

          <div>
            <Label className="mb-1 block text-xs">Couleur (pré-remplie par le type, modifiable)</Label>
            <div className="flex flex-wrap gap-1.5">
              {EVENT_COLORS.map((c) => (
                <button key={c} className={`w-6 h-6 rounded-full transition-transform hover:scale-110 ${color === c ? "ring-2 ring-offset-2 ring-slate-800 scale-110" : ""}`}
                  style={{ backgroundColor: c }} onClick={() => setColor(c)} data-testid={`event-color-${c.slice(1)}`} />
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <Label className="mb-1 block text-xs">Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="event-date-input" />
            </div>
            <div>
              <Label className="mb-1 block text-xs">Fin (si plusieurs jours)</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="event-end-date-input" />
            </div>
            <div>
              <Label className="mb-1 block text-xs">Heure début</Label>
              <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} data-testid="event-start-time" />
            </div>
            <div>
              <Label className="mb-1 block text-xs">Heure fin</Label>
              <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} data-testid="event-end-time" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="mb-1 block text-xs">Statut</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="event-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_META).map(([key, meta]) => (
                    <SelectItem key={key} value={key}>
                      <span className="flex items-center gap-2" style={{ color: meta.color }}>
                        <meta.icon size={13} /> {meta.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1 block text-xs">Priorité</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="event-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PRIORITY_META).map(([key, meta]) => (
                    <SelectItem key={key} value={key}>
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: meta.color }}></span>
                        {meta.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="mb-1 block text-xs">Visibilité</Label>
            <Select value={visibility} onValueChange={setVisibility}>
              <SelectTrigger data-testid="event-visibility-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Visible par tous les utilisateurs</SelectItem>
                <SelectItem value="assigned">Visible uniquement par les personnes assignées</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="mb-1 block text-xs flex items-center gap-1"><MapPin size={11} /> Lieu (optionnel)</Label>
            <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Ex : Studio, 12 rue de la Paix, Paris" data-testid="event-location-input" />
          </div>

          <div data-testid="event-contacts-section">
            <Label className="mb-1.5 block text-xs flex items-center gap-1"><BookUser size={11} /> Contacts / Intervenants (répertoire)</Label>
            <div className="flex gap-2">
              <Select value={contactToAdd} onValueChange={setContactToAdd}>
                <SelectTrigger className="flex-1 min-w-0" data-testid="event-contact-select"><SelectValue placeholder="Associer un contact ou un lieu…" /></SelectTrigger>
                <SelectContent className="max-h-[240px]">
                  {allContacts.filter((c) => !contactIds.includes(c.id)).map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.company_name || `${c.first_name || ""} ${c.last_name || ""}`.trim()}{c.job ? ` — ${c.job}` : c.place_type ? ` — ${c.place_type}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button size="sm" className="h-9 bg-[#26788A] hover:bg-[#1D5E6C] text-white shrink-0"
                onClick={() => { if (contactToAdd) { setContactIds((p) => [...p, contactToAdd]); setContactToAdd(""); } }}
                disabled={!contactToAdd} data-testid="add-event-contact-btn">
                <Plus size={14} />
              </Button>
            </div>
            {contactIds.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {contactIds.map((id) => {
                  const c = allContacts.find((x) => x.id === id);
                  return (
                    <span key={id} className="flex items-center gap-1.5 text-xs font-medium text-slate-700 bg-slate-100 px-2.5 py-1 rounded-full" data-testid={`event-contact-${id}`}>
                      <BookUser size={10} className="text-[#26788A]" />
                      {c ? (c.company_name || `${c.first_name || ""} ${c.last_name || ""}`.trim()) : "Contact"}
                      <button className="text-slate-400 hover:text-red-500" onClick={() => setContactIds((p) => p.filter((x) => x !== id))} data-testid={`remove-event-contact-${id}`}>
                        <X size={11} />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <Label className="mb-1 block text-xs">Description (optionnel)</Label>
            <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="event-description-input" />
          </div>

          <div data-testid="event-equipment-section">
            <Label className="mb-1.5 block text-xs flex items-center gap-1"><Package size={11} /> Matériel à préparer (sacs)</Label>
            <div className="flex gap-2">
              <Select value={equipToAdd} onValueChange={setEquipToAdd}>
                <SelectTrigger className="flex-1 min-w-0" data-testid="event-equipment-select"><SelectValue placeholder="Ajouter du matériel…" /></SelectTrigger>
                <SelectContent className="max-h-[240px]">
                  {allEquipment.filter((e) => !equipmentItems.some((x) => x.equipment_id === e.id)).map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.name} <span className="text-slate-400">({e.available} dispo)</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button size="sm" className="h-9 bg-[#26788A] hover:bg-[#1D5E6C] text-white shrink-0" onClick={addEquipment} disabled={!equipToAdd} data-testid="add-event-equipment-btn">
                <Plus size={14} />
              </Button>
            </div>
            {equipmentItems.length > 0 && (
              <div className="space-y-1 mt-2">
                {equipmentItems.map((x) => (
                  <div key={x.equipment_id} className="flex items-center gap-2 bg-slate-50/70 border border-slate-100 rounded-lg px-2.5 py-1.5" data-testid={`event-equipment-item-${x.equipment_id}`}>
                    <Package size={13} className="text-[#26788A] shrink-0" />
                    <span className="text-xs font-medium text-slate-800 flex-1 truncate">{x.name}</span>
                    <span className="text-[10px] text-slate-400">Qté</span>
                    <Input type="number" min="1" value={x.quantity} onChange={(e) => setEquipQty(x.equipment_id, e.target.value)}
                      className="h-7 w-16 text-xs" data-testid={`event-equipment-qty-${x.equipment_id}`} />
                    <button className="text-slate-300 hover:text-red-500" onClick={() => removeEquipment(x.equipment_id)} data-testid={`remove-event-equipment-${x.equipment_id}`}>
                      <X size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
              <Label className="block text-xs">Attribuer à — disponibilité du {date ? new Date(date + "T00:00:00").toLocaleDateString("fr-FR") : ""}</Label>
              <label className="flex items-center gap-1.5 text-[10px] font-medium text-amber-700 cursor-pointer" data-testid="force-assign-toggle">
                <input type="checkbox" checked={forceAssign} onChange={(e) => setForceAssign(e.target.checked)} className="accent-amber-500 w-3.5 h-3.5" data-testid="force-assign-checkbox" />
                Forcer l'attribution (exceptionnel)
              </label>
            </div>
            <div className="space-y-1 max-h-48 overflow-y-auto border border-slate-100 rounded-lg p-1.5">
              {availability.length === 0 && <p className="text-xs text-slate-400 text-center py-2">Aucun employé actif</p>}
              {availability.map((emp) => {
                const badge = STATUS_BADGE[emp.status];
                const Icon = badge.icon;
                const checked = employeeIds.includes(emp.employee_id);
                const working = emp.status === "work";
                const locked = !working && !checked && !forceAssign;
                return (
                  <label key={emp.employee_id}
                    className={`flex items-center gap-2.5 p-2 rounded-lg transition-colors ${checked ? "bg-teal-50" : locked ? "" : "hover:bg-slate-50"} ${!working ? "opacity-45" : ""} ${locked ? "cursor-not-allowed" : "cursor-pointer"}`}
                    title={locked ? "Ne travaille pas ce jour — cochez « Forcer l'attribution » pour l'assigner quand même" : undefined}
                    data-testid={`assign-emp-${emp.employee_id}`}>
                    <input type="checkbox" checked={checked} disabled={locked} onChange={() => !locked && toggleEmployee(emp.employee_id)}
                      className="accent-[#26788A] w-4 h-4" data-testid={`assign-checkbox-${emp.employee_id}`} />
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: emp.color }}></span>
                    <span className="min-w-0 text-sm font-medium text-slate-800 flex-1 break-words">{emp.first_name} {emp.last_name}</span>
                    <span className={`flex items-center gap-1 text-[10px] max-w-[45%] px-2 py-0.5 rounded-full font-medium ${badge.className}`}>
                      <Icon size={10} /> {emp.detail}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
          <EventAttachments eventId={event?.id || createdEvent?.id} canManage pending={pendingFiles} onPendingChange={setPendingFiles} disabled={saving} onBusyChange={setFileBusy} refreshKey={attachmentRefresh} />
          {/* Suivi des attributions (accepté/refusé/terminé + durées) */}
          {event && (event.assignments || []).length > 0 && (
            <div className="border-t border-slate-100 pt-3" data-testid="assignment-tracking">
              <Label className="mb-1.5 block text-xs">Suivi des attributions</Label>
              <div className="space-y-1.5">
                {event.assignments.map((a) => {
                  const emp = availability.find((e) => e.employee_id === a.employee_id);
                  const name = emp ? `${emp.first_name} ${emp.last_name}` : "Employé";
                  const meta = {
                    pending: { label: "Pas encore commencée", cls: "bg-slate-100 text-slate-500" },
                    accepted: { label: "En cours", cls: "bg-amber-100 text-amber-700" },
                    refused: { label: "Refusée", cls: "bg-red-100 text-red-600" },
                    done: { label: "Terminée", cls: "bg-green-100 text-green-700" },
                  }[a.status] || { label: a.status, cls: "bg-slate-100 text-slate-500" };
                  const fmtT = (s) => s ? new Date(s).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : null;
                  const dur = a.duration_minutes != null ? (a.duration_minutes >= 60 ? `${Math.floor(a.duration_minutes / 60)}h${String(a.duration_minutes % 60).padStart(2, "0")}` : `${a.duration_minutes} min`) : null;
                  return (
                    <div key={a.employee_id} className="flex flex-wrap items-center gap-2 text-xs bg-slate-50/60 border border-slate-100 rounded-lg px-2.5 py-1.5" data-testid={`assignment-${a.employee_id}`}>
                      {emp && <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: emp.color }}></span>}
                      <span className="font-medium text-slate-800">{name}</span>
                      <span className={`px-2 py-0.5 rounded-full font-medium ${meta.cls}`}>{meta.label}</span>
                      {a.status === "accepted" && a.accepted_at && <LiveChrono since={a.accepted_at} base={a.time_spent_seconds || 0} />}
                      {a.accepted_at && <span className="text-slate-400">Commencée : {fmtT(a.accepted_at)}</span>}
                      {a.finished_at && <span className="text-slate-400">Terminée : {fmtT(a.finished_at)}</span>}
                      {dur && <span className="font-semibold text-[#26788A]">Durée : {dur}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="sticky -bottom-5 bg-background border-t border-slate-200 pt-3 pb-2 flex-row flex-wrap items-center justify-end gap-2 sm:space-x-0" data-testid="event-dialog-actions">
          {event && (
            <Button variant="ghost" className="text-red-500 hover:text-red-600 mr-auto" onClick={handleDelete} data-testid="delete-event-btn">
              <Trash2 size={14} className="mr-1" /> Supprimer
            </Button>
          )}
          {event && (
            <Button variant="outline" className="text-[#26788A] border-[#26788A]/30" onClick={downloadCallsheet} data-testid="callsheet-btn">
              <FileDown size={14} className="mr-1" /> Fiche PDF
            </Button>
          )}
          <Button variant="outline" className="min-h-11" disabled={saving} onClick={onClose} data-testid="cancel-event-btn">Annuler</Button>
          <Button className="min-h-11 bg-[#26788A] hover:bg-[#1D5E6C] text-white" onClick={handleSave} disabled={saving || fileBusy} data-testid="save-event-btn">
            {saving && <Loader2 size={14} className="animate-spin mr-1" />}
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const EventDialog = props => {
  const { user } = useAuth();
  return user?.role === "admin" || user?.can_manage_events ? <EventEditor {...props} /> : <EventDetailsDialog {...props} />;
};
